import React, { ChangeEvent, KeyboardEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Box, Flex, Image, Input, InputGroup, InputLeftElement, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import iconWindowsDevice from "@/assets/IconWindowsDevice.svg?url";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { colours, qoriaColours } from "@/theme/colours";
import { useSignatureStore } from "@/stores/v2/signatureStore";
import { buildRuleTargetOptions, RulePickerTargetType, RuleTargetOption } from "./utils";
import { scrollbarProps } from "../styles";
import { collectRuleFaviconUrls, preloadFavicons } from "../utils";

interface Props {
	value: string;
	onChange: (value: string, optionType?: RulePickerTargetType) => void;
	onEnter?: () => void;
	isDisabled?: boolean;
	/** When true, native app controls appear in the dropdown. Lists omit this. */
	includeAppControls?: boolean;
}

export function RuleTargetPicker(props: Props): JSX.Element {
	const { value, onChange, onEnter, isDisabled = false, includeAppControls = false } = props;
	const { t } = useTranslation();

	const [filter, setFilter] = useState("");
	const [isOpen, setIsOpen] = useState(false);
	const [highlightedIndex, setHighlightedIndex] = useState(-1);

	const inputRef = useRef<HTMLInputElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const reactId = useId();
	const listboxId = `${reactId}-listbox`;

	function optionId(index: number): string {
		return `${listboxId}-option-${index}`;
	}

	const signatures = useSignatureStore((state) => state.signatures);
	const appControls = useSignatureStore((state) => state.appControls);
	const findSignature = useSignatureStore((state) => state.findSignature);
	const findAppControl = useSignatureStore((state) => state.findAppControl);

	const dropdownOptions = useMemo(
		() =>
			buildRuleTargetOptions({
				signatures,
				appControls,
				includeAppControls,
				filter,
				t,
			}),
		[signatures, appControls, includeAppControls, filter, t]
	);

	const selectableOptions = useMemo(() => dropdownOptions.filter((option) => option.type !== "section"), [dropdownOptions]);

	const displayValue = useMemo(() => {
		if (isOpen) {
			return filter;
		}
		if (value === "") {
			return "";
		}

		const signature = findSignature(value);
		if (signature) {
			return signature.name;
		}

		const appControl = findAppControl(value);
		if (appControl) {
			return appControl.name;
		}

		return value;
	}, [isOpen, filter, value, findSignature, findAppControl]);

	const handleClickOutside = useCallback((event: MouseEvent) => {
		if (
			inputRef.current &&
			!inputRef.current.contains(event.target as Node) &&
			dropdownRef.current &&
			!dropdownRef.current.contains(event.target as Node)
		) {
			setIsOpen(false);
			setFilter("");
			setHighlightedIndex(-1);
		}
	}, []);

	useEffect(() => {
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [handleClickOutside]);

	function handleSelect(option: RuleTargetOption) {
		if (option.type === "section") {
			return;
		}

		const { getFavicon, findAppControl } = useSignatureStore.getState();
		preloadFavicons(
			collectRuleFaviconUrls([{ target: option.value, type: option.type }], {
				getFavicon,
				findAppControlFavicon: (id) => findAppControl(id)?.favicon || undefined,
			})
		);

		onChange(option.value, option.type);
		setFilter("");
		setIsOpen(false);
		setHighlightedIndex(-1);
		inputRef.current?.blur();
	}

	function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
		const nextFilter = event.target.value;
		setFilter(nextFilter);
		setIsOpen(true);
		setHighlightedIndex(-1);

		if (nextFilter === "" || (value !== "" && nextFilter !== displayValue)) {
			onChange("");
		}
	}

	function handleInputFocus() {
		setFilter("");
		setIsOpen(true);
		setHighlightedIndex(-1);
	}

	function handleInputClick() {
		setIsOpen(true);
	}

	function handleInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
		if (event.key === "Escape") {
			event.stopPropagation();
			setIsOpen(false);
			setFilter("");
			setHighlightedIndex(-1);
			inputRef.current?.blur();
			return;
		}

		if (event.key === "ArrowDown") {
			event.preventDefault();
			setIsOpen(true);
			setHighlightedIndex((prev) => Math.min(prev + 1, selectableOptions.length - 1));
			return;
		}

		if (event.key === "ArrowUp") {
			event.preventDefault();
			setHighlightedIndex((prev) => Math.max(prev - 1, 0));
			return;
		}

		if (event.key === "Enter") {
			event.preventDefault();
			if (highlightedIndex >= 0 && selectableOptions[highlightedIndex]) {
				handleSelect(selectableOptions[highlightedIndex]);
				return;
			}
			if (selectableOptions.length === 1) {
				handleSelect(selectableOptions[0]);
				return;
			}
			onEnter?.();
		}
	}

	let selectableIndex = -1;

	return (
		<Box position="relative">
			<InputGroup>
				<InputLeftElement pointerEvents="none" height="full">
					<ChakraFAIcon icon="fa-link" variant="regular" color={colours.text.regular} boxSize={5} />
				</InputLeftElement>
				<Input
					ref={inputRef}
					role="combobox"
					aria-expanded={isOpen}
					aria-controls={listboxId}
					aria-autocomplete="list"
					aria-activedescendant={highlightedIndex >= 0 ? optionId(highlightedIndex) : undefined}
					aria-haspopup="listbox"
					value={displayValue}
					onChange={handleInputChange}
					onFocus={handleInputFocus}
					onClick={handleInputClick}
					onKeyDown={handleInputKeyDown}
					placeholder={t("rulesLibrary.form.addItemPlaceholder")}
					autoComplete="off"
					isDisabled={isDisabled}
					pl={10}
				/>
			</InputGroup>

			{isOpen && (
				<Box
					ref={dropdownRef}
					id={listboxId}
					role="listbox"
					position="absolute"
					top="100%"
					left={0}
					right={0}
					mt={1}
					bg={qoriaColours.brand.neutral[10]}
					border="1px solid"
					borderColor={qoriaColours.brand.neutral[100]}
					borderRadius="md"
					boxShadow="md"
					maxH="300px"
					overflowY="auto"
					overscrollBehavior="contain"
					zIndex={10}
					sx={scrollbarProps}
				>
					{dropdownOptions.length === 0 ? (
						<Text px={4} py={3} fontSize="sm" color={colours.text.regular}>
							{t("rulesLibrary.form.noResults")}
						</Text>
					) : (
						dropdownOptions.map((option) => {
							if (option.type === "section") {
								return (
									<Flex key={option.value} align="center" gap={2} px={4} py={2} bg={qoriaColours.brand.neutral[50]}>
										<Text fontSize="sm" fontWeight={700} color={colours.text.regular}>
											{option.label}
										</Text>
										{option.value === "section-app-controls" && (
											<Image src={iconWindowsDevice} alt="" boxSize="12px" flexShrink={0} aria-hidden />
										)}
									</Flex>
								);
							}

							selectableIndex += 1;
							const isHighlighted = selectableIndex === highlightedIndex;

							return (
								<Box
									key={`${option.type}-${option.value}`}
									id={optionId(selectableIndex)}
									role="option"
									aria-selected={isHighlighted}
									px={4}
									py={2}
									cursor="pointer"
									bg={isHighlighted ? qoriaColours.brand.neutral[50] : undefined}
									_hover={{ bg: qoriaColours.brand.neutral[50] }}
									onMouseDown={(event) => {
										event.preventDefault();
										handleSelect(option);
									}}
								>
									<Text fontSize="sm" color={colours.text.title}>
										{option.label}
									</Text>
								</Box>
							);
						})
					)}
				</Box>
			)}
		</Box>
	);
}
